// Shared utilities for /nursing — MIT Nursing College (GS Mandal)
// Visual identity: Navy primary, Teal/Mint accent, white/light-sky surfaces — healthcare aesthetic.
import React, { useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export const NURSING_COLORS = {
  navy: "#0F2A5F",
  navyDark: "#0A1F47",
  navyLight: "#1B3E83",
  teal: "#0FB28A",
  tealDark: "#0E916F",
  tealLight: "#E0F5EE",
  sky: "#F4F9FF",
  border: "#E2E8F0",
  ink: "#0F172A",
  inkSoft: "#475569",
};

export function slugify(name) {
  return String(name || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function formatDate(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
}

export function PageHero({ eyebrow, title, subtitle, accent = "navy" }) {
  const bg = accent === "teal"
    ? "from-[#0FB28A] via-[#0E916F] to-[#0F2A5F]"
    : "from-[#0F2A5F] via-[#1B3E83] to-[#0A1F47]";
  return (
    <section className={`relative bg-gradient-to-br ${bg} text-white py-16 md:py-24 overflow-hidden`} data-testid="nur-page-hero">
      <div className="absolute inset-0 opacity-[0.08]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '24px 24px',
      }} />
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#0FB28A]/30 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4">
        {eyebrow && (
          <div className="text-[10px] tracking-[0.4em] uppercase font-semibold text-[#7FE7CE] mb-3">
            {eyebrow}
          </div>
        )}
        <h1 className="font-['Poppins',sans-serif] font-bold text-4xl md:text-6xl tracking-tight max-w-4xl leading-[1.05]">{title}</h1>
        {subtitle && (
          <p className="mt-4 max-w-2xl text-white/85 text-base md:text-lg">{subtitle}</p>
        )}
      </div>
    </section>
  );
}

export function PageBody({ children, className = "" }) {
  return (
    <section className={`max-w-7xl mx-auto px-4 py-12 md:py-16 ${className}`}>{children}</section>
  );
}

export function Lightbox({ photos, idx, onIdx, onClose }) {
  const cur = photos[idx];
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onIdx((idx - 1 + photos.length) % photos.length);
      if (e.key === "ArrowRight") onIdx((idx + 1) % photos.length);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [idx, photos.length, onClose, onIdx]);
  if (!cur) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={onClose} data-testid="nur-lightbox">
      <button onClick={onClose} className="absolute top-5 right-5 text-white/80 hover:text-white p-2" aria-label="Close"><X size={22} /></button>
      {photos.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); onIdx((idx - 1 + photos.length) % photos.length); }} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 bg-black/30 rounded-full" aria-label="Previous"><ChevronLeft size={24} /></button>
          <button onClick={(e) => { e.stopPropagation(); onIdx((idx + 1) % photos.length); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-3 bg-black/30 rounded-full" aria-label="Next"><ChevronRight size={24} /></button>
        </>
      )}
      <figure className="max-w-5xl max-h-full" onClick={(e) => e.stopPropagation()}>
        <img src={cur.image_url || cur.url} alt={cur.caption || ""} className="max-w-full max-h-[80vh] object-contain" />
        {cur.caption && <figcaption className="mt-3 text-center text-white/90 text-sm">{cur.caption}</figcaption>}
      </figure>
    </div>
  );
}

export function renderInstituteHtml(content) {
  if (!content) return null;
  const isHtml = /<\/?[a-z][\s\S]*?>/i.test(content);
  return isHtml ? content
    : content.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");
}

export const STATIC_CONTENT = {
  about_intro:
    "MIT Nursing College — a flagship institution of Gramaudyogik Shikshan Mandal — prepares compassionate, competent, and committed healthcare professionals. We combine rigorous classroom learning with structured clinical exposure across leading hospitals, modern simulation laboratories, and faculty mentorship — graduating nurses who lead at every level of India's evolving healthcare system.",
  vision:
    "To be a centre of excellence in nursing education and applied healthcare practice — graduating professionals whose technical mastery is matched by empathy, ethics, and a lifelong commitment to community wellbeing.",
  mission:
    "Deliver an outcome-based, INC-aligned curriculum across ANM, GNM, B.Sc, Post Basic B.Sc, and M.Sc nursing programmes; provide hands-on training across reputed hospital partners and modern simulation labs; foster research, leadership, and clinical decision-making; and place every graduate in a meaningful healthcare role.",
};
